import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useFormik } from "formik"
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import toastr from "toastr"
import { updateStoreFiscalInformation } from "../../services/storeFiscalService"
import { useStore } from "../../contexts/StoreContext"
import { fiscalValidationSchema } from "./fiscalValidation"

const fields = [
  { name: "legalName", label: "Raison sociale", placeholder: "Boulangerie Exemple SARL", md: 7 },
  { name: "legalForm", label: "Forme juridique", placeholder: "SARL, SARL AU, SA...", md: 5 },
  { name: "ice", label: "Identifiant commun de l’entreprise (ICE)", placeholder: "15 chiffres", md: 6 },
  { name: "fiscalIdentifier", label: "Identifiant fiscal", md: 6 },
  { name: "commercialRegisterNumber", label: "Numéro du registre de commerce", md: 6 },
  { name: "professionalTaxNumber", label: "Numéro de taxe professionnelle", md: 6 },
  { name: "cnssNumber", label: "Numéro CNSS", optional: true, md: 6 },
]

const FiscalInformationModal = ({ isOpen, toggle }) => {
  const { store, refreshStore } = useStore()
  const [genericError, setGenericError] = useState("")

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: fields.reduce((values, field) => {
      values[field.name] = store?.[field.name] || ""
      return values
    }, {}),
    validationSchema: fiscalValidationSchema,
    onSubmit: async (values, helpers) => {
      setGenericError("")
      try {
        await updateStoreFiscalInformation(store, values)
        await refreshStore()
        toastr.success("Vos informations fiscales ont été transmises avec succès.")
        toggle()
      } catch (error) {
        const violations = error.response?.data?.violations
        if (error.response?.status === 422 && Array.isArray(violations)) {
          violations.forEach(({ propertyPath, message }) => {
            helpers.setFieldTouched(propertyPath, true, false)
            helpers.setFieldError(propertyPath, message)
          })
        } else {
          setGenericError("Impossible d’enregistrer vos informations fiscales. Veuillez réessayer.")
        }
      } finally {
        helpers.setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (isOpen) setGenericError("")
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="fiscal-modal">
      <ModalHeader toggle={toggle}>Informations fiscales du commerce</ModalHeader>
      <Form onSubmit={formik.handleSubmit} noValidate>
        <ModalBody>
          <div className="fiscal-modal-intro mb-4">
            <span className="fiscal-modal-icon"><i className="bx bx-shield-quarter" /></span>
            <div>
              <h5 className="mb-1">Préparez votre commerce à publier</h5>
              <p className="text-muted mb-0">Ces informations seront contrôlées avant l’activation de la publication des paniers.</p>
            </div>
          </div>

          {genericError && <Alert color="danger">{genericError}</Alert>}

          <Row>
            {fields.map(field => {
              const hasError = formik.touched[field.name] && Boolean(formik.errors[field.name])
              return (
                <Col md={field.md} key={field.name}>
                  <FormGroup className="mb-3">
                    <Label htmlFor={`fiscal-${field.name}`}>
                      {field.label} {field.optional && <span className="text-muted">(facultatif)</span>}
                    </Label>
                    <Input
                      id={`fiscal-${field.name}`}
                      name={field.name}
                      value={formik.values[field.name]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder={field.placeholder}
                      invalid={hasError}
                    />
                    {hasError && <FormFeedback>{formik.errors[field.name]}</FormFeedback>}
                  </FormGroup>
                </Col>
              )
            })}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={toggle} disabled={formik.isSubmitting}>Annuler</Button>
          <Button type="submit" color="primary" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? "Transmission..." : "Transmettre pour validation"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

FiscalInformationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
}

export default FiscalInformationModal
